import type { RouteObject } from 'react-router-dom';

import ErrorBoundary from './ErrorBoundary';

export enum RouteID {
  Root = 'root',
  Login = 'login',
  Home = 'home',
  Instruction = 'instruction',
  Legal = 'legal',
  Premium = 'premium',
  Profile = 'profile',
  TelegramBots = 'telegram-bots',
  TelegramBotMenuRoot = 'telegram-bot-menu-root',
  TelegramBotMenuVariables = 'telegram-bot-menu-variables',
  TelegramBotMenuUsers = 'telegram-bot-menu-users',
  TelegramBotMenuDatabase = 'telegram-bot-menu-database',
  TelegramBotMenuConstructor = 'telegram-bot-menu-constructor',
  NotFound = 'not-found',
}

export const routes: RouteObject[] = [
  {
    id: RouteID.Root,
    path: '/:lang?/',
    ErrorBoundary,
    async lazy() {
      const [component, loader] = await Promise.all([
        await import('./Root'),
        await import('./Root/loader'),
      ]);

      return {
        Component: component.default,
        loader: loader.default,
      };
    },
    shouldRevalidate: () => true,
    children: [
      {
        id: RouteID.Login,
        path: 'login/',
        async lazy() {
          const [component, loader] = await Promise.all([
            await import('./Login'),
            await import('./Login/loader'),
          ]);

          return {
            Component: component.default,
            loader: loader.default,
          };
        },
      },
      {
        id: RouteID.Home,
        index: true,
        async lazy() {
          const [component, loader] = await Promise.all([
            await import('./Home'),
            await import('./Home/loader'),
          ]);

          return {
            Component: component.default,
            loader: loader.default,
          };
        },
      },
      {
        id: RouteID.Instruction,
        path: 'instruction/',
        async lazy() {
          const [component, loader] = await Promise.all([
            await import('./Instruction'),
            await import('./Instruction/loader'),
          ]);

          return {
            Component: component.default,
            loader: loader.default,
          };
        },
      },
      {
        id: RouteID.Legal,
        path: 'legal/:type/',
        async lazy() {
          const [component, loader] = await Promise.all([
            await import('./Legal'),
            await import('./Legal/loader'),
          ]);

          return {
            Component: component.default,
            loader: loader.default,
          };
        },
      },
      {
        id: RouteID.Premium,
        path: 'premium/',
        async lazy() {
          const [component, loader] = await Promise.all([
            await import('./Premium'),
            await import('./Premium/loader'),
          ]);

          return {
            Component: component.default,
            loader: loader.default,
          };
        },
      },
      {
        async lazy() {
          const module = await import('./AuthRequired/Root');

          return { Component: module.default };
        },
        children: [
          {
            id: RouteID.Profile,
            path: 'profile/',
            async lazy() {
              const [component, loader] = await Promise.all([
                await import('./AuthRequired/Profile'),
                await import('./AuthRequired/Profile/loader'),
              ]);

              return {
                Component: component.default,
                loader: loader.default,
              };
            },
          },
          {
            id: RouteID.TelegramBots,
            path: 'telegram-bots/',
            async lazy() {
              const [component, loader] = await Promise.all([
                await import('./AuthRequired/TelegramBots'),
                await import('./AuthRequired/TelegramBots/loader'),
              ]);

              return {
                Component: component.default,
                loader: loader.default,
              };
            },
          },
          {
            id: RouteID.TelegramBotMenuRoot,
            path: 'telegram-bot-menu/:telegramBotID/',
            async lazy() {
              const loader = await import('./AuthRequired/TelegramBotMenu/Root/loader');

              return { loader: loader.default };
            },
            children: [
              {
                id: RouteID.TelegramBotMenuVariables,
                path: 'variables/',
                async lazy() {
                  const [component, loader] = await Promise.all([
                    await import('./AuthRequired/TelegramBotMenu/Variables'),
                    await import('./AuthRequired/TelegramBotMenu/Variables/loader'),
                  ]);

                  return {
                    Component: component.default,
                    loader: loader.default,
                  };
                },
              },
              {
                id: RouteID.TelegramBotMenuUsers,
                path: 'chats/',
                async lazy() {
                  const [component, loader] = await Promise.all([
                    await import('./AuthRequired/TelegramBotMenu/Users'),
                    await import('./AuthRequired/TelegramBotMenu/Users/loader'),
                  ]);

                  return {
                    Component: component.default,
                    loader: loader.default,
                  };
                },
              },
              {
                id: RouteID.TelegramBotMenuDatabase,
                path: 'database/',
                async lazy() {
                  const [component, loader] = await Promise.all([
                    await import('./AuthRequired/TelegramBotMenu/Database'),
                    await import('./AuthRequired/TelegramBotMenu/Database/loader'),
                  ]);

                  return {
                    Component: component.default,
                    loader: loader.default,
                  };
                },
              },
              {
                id: RouteID.TelegramBotMenuConstructor,
                path: 'constructor/',
                async lazy() {
                  const [component, loader] = await Promise.all([
                    await import('./AuthRequired/TelegramBotMenu/Constructor'),
                    await import('./AuthRequired/TelegramBotMenu/Constructor/loader'),
                  ]);

                  return {
                    Component: component.default,
                    loader: loader.default,
                  };
                },
              },
            ],
          },
        ],
      },
      {
        id: RouteID.NotFound,
        path: '*',
        async lazy() {
          const loader = await import('./NotFound/loader');

          return { loader: loader.default };
        },
      },
    ],
  },
];
